import { Button } from '@material-ui/core'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  EnumEstadoOperacionBuyAndSell,
  EnumOperationState,
  EnumTipoOperacion,
  EnumEstadoOperacionConciliar,
  EnumEstadoOperacionBuyAndSellSearchFilter
} from '../../api/oData/enums'
import {
  add,
  cancel,
  cancelMassive,
  checkOperationsState,
  deleteMassive,
  deleteOperation,
  edit,
  fetchBuyAndSellListItemsByIds,
  fetchLogisticElementsToItems,
  fetchSubjectsByTypeToItems,
  filterApplicableActionsConfiguration,
  GetCanCancelMassive,
  GetCanDeleteMassive,
  GetCanSendMassive,
  fetchTradesByCode,
  addByTrade,
  sendOperationsMassive,
  searchBuyAndSellOperations
} from './buyandsell.service'

import {
  ColumnFilter,
  DataTable,
  DataTableColumn,
  DataType,
  DecimalScale,
  FilterType,
  FormDialogMode,
  Input,
  SelectOption,
  useNotify,
  UserPermission
} from '@globaleris/ui.library'
import { enumToSelectOptionArray, enumToSelectOptionArrayDefaultValues } from '../../helpers'

import BlockIcon from '@material-ui/icons/Block'
import SendIcon from '@material-ui/icons/Send'
import moment from 'moment'

import CustomFilter from '../../components/shared/customFilter/customFilter'
import DateFilter from '../../components/shared/dateFilter/dateFilter'
import { generateIconsFromConfiguration } from '../../helpers/dataTableActionIconHelper'
import { ActionIcons, DateFilters } from '../../model/enums'

import { Add, Delete, Edit, PlusOne } from '@material-ui/icons'
import { useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'
import { NotificationMessageIcons } from '../../components'
import { ErrorDetailList } from '../../components/errorDetailList/errorDetailList'
import { DataTableErrorHandler } from '../../components/errors/scopes/dataTable/dataTableError'
import Page from '../../components/page/page'
import PageHeader from '../../components/page/pageHeader'
import { normalizeFilename } from '../../helpers/fileDownloadHelper'
import { calculateGasDay, getGasDay } from '../../helpers/gasDayHelper'
import { getUserPermissions } from '../../helpers/permissionsHelper'
import { usePageState } from '../../hooks'
import { useConfirmationDialog } from '../../hooks/useConfirmationDialog'
import { useErrorDetailListDialog } from '../../hooks/useErrorDetailListDialog'
import { FormDialogDefinitionProps, useFormDialog } from '../../hooks/useFormDialog'
import { useHttpError } from '../../hooks/useHttpError'
import useRetries from '../../hooks/useRetries'
import { DatePeriod, GlobalNotification } from '../../model'
import { ConfirmationDialogs } from '../../model/enums/confirmationDialogs'
import { Pages } from '../../model/RBAC/constants'
import { ServiceBatchResponse } from '../../model/serviceBatchResponse'
import {
  getCustomerDomain,
  notifyError,
  RootStoreModel,
  storeCustomFilterForCurrentPage,
  storeDataTableFilterForCurrentPage,
  storeDataTableRowsPerPageForCurrentPage,
  storeDataTableSearchForCurrentPage,
  storeDateFilterForCurrentPage
} from '../../redux'
import { AddMassiveBuyAndSellModel, BuyAndSellModel } from './buyandsell.model'
import CustomFilterV2 from '../../components/shared/customFilter/customFilterV2'
import { EnumOperationType } from '../../api'

export interface BuyAndSellPageProps {}

export const BuyAndSellPage: React.FC<any> = props => {
  const [t] = useTranslation(['globalerisLibrary', 'enums', 'buyAndSell', 'errorDetailList'])
  let history = useHistory()
  const { info } = useNotify()

  const { dataTableFilter, dataTableSearch, dateFilter, customFilter, dataTableRowsPerPage } =
    usePageState()
  const activeCompany = useSelector(
    (state: RootStoreModel) => state.profile?.activeCompany && state.profile?.activeCompany
  )
  const activeBalanceZone = useSelector(
    (state: RootStoreModel) => state.profile?.activeBalanceZone && state.profile?.activeBalanceZone
  )

  const [globalNotification, setGlobalNotification] = React.useState<
    GlobalNotification | undefined
  >(undefined)

  const [dateRange, setDateRange] = React.useState<DatePeriod>({
    start: dateFilter?.initDate || moment().toDate(),
    end: dateFilter?.endDate || moment().toDate()
  })

  const [operations, setOperations] = useState<BuyAndSellModel[] | null>(null)
  const [dataTableKey, setDataTableKey] = useState<number>(0)
  const [flanges] = useState<number[]>(props.location.state ? props.location.state.ids : [])

  const [isFetching, setIsFetching] = useState<boolean>(true)

  interface formDependenciesProps {
    formState: any
    logisticElements: SelectOption[]
    counterparts: SelectOption[]
  }

  const [formDependencies, setFormDependencies] = React.useState<formDependenciesProps>({
    formState: {},
    logisticElements: [],
    counterparts: []
  })

  //#region Form definitions

  const addFormDefinition =
    React.useMemo<FormDialogDefinitionProps>((): FormDialogDefinitionProps => {
      return {
        id: 'ADD',
        inputs: [
          {
            name: 'date',
            label: t('buyAndSell:forms.add.inputs.date'),
            type: DataType.DATE,
            required: true,
            minDate: getGasDay().toISOString(),
            defaultValue: moment(dateRange.start).isBefore(moment()) ? new Date() : dateRange.start
          },
          {
            name: 'operationType',
            label: t('buyAndSell:forms.add.inputs.operationType'),
            hideForResponsive: true,
            type: DataType.SELECT,
            selectOptions: enumToSelectOptionArrayDefaultValues(
              EnumTipoOperacion,
              'enums:EnumTipoOperacion',
              [EnumTipoOperacion.Compra, EnumTipoOperacion.Venta]
            ),
            required: true
          },
          {
            name: 'logisticElement.description',
            label: t('buyAndSell:forms.add.inputs.logisticElement'),
            type: DataType.SELECT,
            selectOptions: formDependencies.logisticElements,
            required: true
          },
          {
            name: 'quantity',
            label: t('buyAndSell:forms.add.inputs.quantity'),
            type: DataType.NUMBER,
            required: true,
            decimalScale: DecimalScale.ENERGY
          },
          {
            name: 'counterpart',
            label: t('buyAndSell:forms.add.inputs.counterpart'),
            type: DataType.SELECT,
            selectOptions: formDependencies.counterparts,
            required: true
          },
          {
            name: 'codShip',
            label: t('buyAndSell:forms.add.inputs.codShip'),
            type: DataType.TEXT,
            required: false
          }
        ]
      }
    }, [formDependencies, dateRange.start, t])

  const addMassiveFormDefinition =
    React.useMemo<FormDialogDefinitionProps>((): FormDialogDefinitionProps => {
      return {
        id: 'ADD_MASSIVE',
        inputs: [
          {
            name: 'startDate',
            label: t('buyAndSell:forms.addMassive.inputs.startDate'),
            type: DataType.DATE,
            required: true,
            minDate: getGasDay().toISOString(),
            defaultValue: moment(dateRange.start).isBefore(moment()) ? new Date() : dateRange.start,
            onChange: (value, formState: any) => {
              if (!moment(value).isValid()) {
                return
              }
              if (formState.endDate && moment(formState.endDate).isBefore(moment(value))) {
                setFormDependencies({
                  ...formDependencies,
                  formState: {
                    ...formState,
                    startDate: moment(value).toDate(),
                    endDate: moment(formState.startDate).toDate()
                  }
                })
              } else {
                setFormDependencies({
                  ...formDependencies,
                  formState: {
                    ...formState,
                    startDate: moment(value).toDate(),
                    endDate:
                      formState.endDate !== undefined
                        ? moment(formState.endDate).toDate()
                        : undefined
                  }
                })
              }
            }
          },
          {
            name: 'endDate',
            label: t('buyAndSell:forms.addMassive.inputs.endDate'),
            type: DataType.DATE,
            required: true,
            minDate: formDependencies.formState?.startDate
              ? formDependencies.formState?.startDate?.toISOString()
              : moment().toISOString(),
            maxDate: formDependencies.formState?.startDate
              ? moment(formDependencies.formState?.startDate).add(3, 'months').toISOString()
              : moment().add(3, 'months').toISOString(),
            defaultValue: moment(dateRange.end).isBefore(moment(dateRange.start))
              ? dateRange.start
              : dateRange.end,
            onChange: (value, formState: any) => {
              if (!moment(value).isValid()) {
                return
              }

              if (formState.startDate && moment(value).isBefore(moment(formState.startDate))) {
                setFormDependencies({
                  ...formDependencies,
                  formState: { ...formState, endDate: moment(formState.startDate).toDate() }
                })
              } else {
                setFormDependencies({
                  ...formDependencies,
                  formState: {
                    ...formState,
                    endDate: moment(value).toDate(),
                    startDate:
                      formState.startDate !== undefined
                        ? moment(formState.startDate).toDate()
                        : undefined
                  }
                })
              }
            }
          },
          {
            name: 'operationType.id',
            label: t('buyAndSell:forms.addMassive.inputs.operationType'),
            hideForResponsive: true,
            type: DataType.SELECT,
            selectOptions: enumToSelectOptionArrayDefaultValues(
              EnumTipoOperacion,
              'enums:EnumTipoOperacion',
              [EnumTipoOperacion.Compra, EnumTipoOperacion.Venta]
            ),
            required: true
          },
          {
            name: 'logisticElement.description',
            label: t('buyAndSell:forms.addMassive.inputs.logisticElement'),
            type: DataType.SELECT,
            selectOptions: formDependencies.logisticElements,
            required: true
          },
          {
            name: 'quantity',
            label: t('buyAndSell:forms.addMassive.inputs.quantity'),
            type: DataType.NUMBER,
            required: true,
            decimalScale: DecimalScale.ENERGY
          },
          // {
          //   name: 'price',
          //   label: t('buyAndSell:forms.addMassive.inputs.price'),
          //   type: DataType.NUMBER,
          //   decimalScale: DecimalScale.CURRENCY
          // },
          {
            name: 'counterpart.id',
            label: t('buyAndSell:forms.addMassive.inputs.counterpart'),
            type: DataType.SELECT,
            selectOptions: formDependencies.counterparts,
            required: true
          }
        ]
      }
    }, [formDependencies, dateRange.start, dateRange.end, t])

  const editFormDefinition =
    React.useMemo<FormDialogDefinitionProps>((): FormDialogDefinitionProps => {
      return {
        id: 'EDIT',
        inputs: [
          {
            name: 'date',
            label: t('buyAndSell:forms.edit.inputs.date'),
            type: DataType.DATE,
            required: true
          },
          {
            name: 'operationType',
            label: t('buyAndSell:forms.edit.inputs.operationType'),
            hideForResponsive: true,
            type: DataType.SELECT,
            selectOptions: enumToSelectOptionArrayDefaultValues(
              EnumTipoOperacion,
              'enums:EnumTipoOperacion',
              [EnumTipoOperacion.Compra, EnumTipoOperacion.Venta]
            ),
            required: true
          },
          {
            name: 'logisticElement.id',
            label: t('buyAndSell:forms.edit.inputs.logisticElement'),
            type: DataType.SELECT,
            selectOptions: formDependencies.logisticElements,
            required: true
          },
          {
            name: 'quantity',
            label: t('buyAndSell:forms.edit.inputs.quantity'),
            type: DataType.NUMBER,
            required: true,
            decimalScale: DecimalScale.ENERGY
          },
          // {
          //   name: 'price',
          //   label: t('buyAndSell:forms.edit.inputs.price'),
          //   type: DataType.NUMBER,
          //   decimalScale: DecimalScale.CURRENCY
          // },
          {
            name: 'counterpart.id',
            label: t('buyAndSell:forms.edit.inputs.counterpart'),
            type: DataType.SELECT,
            selectOptions: formDependencies.counterparts
          },
          {
            name: 'codTrade',
            label: t('buyAndSell:forms.edit.inputs.tradeCode'),
            type: DataType.TEXT,
            readOnly: true
          },
          {
            name: 'codShip',
            label: t('buyAndSell:forms.edit.inputs.codShip'),
            type: DataType.TEXT,
            readOnly: false
          }
        ]
      }
    }, [formDependencies, t])

  const editDraftFormDefinition =
    React.useMemo<FormDialogDefinitionProps>((): FormDialogDefinitionProps => {
      return {
        id: 'EDIT_DRAFT',
        inputs: [
          {
            name: 'date',
            label: t('buyAndSell:forms.editDraft.inputs.date'),
            type: DataType.DATE,
            required: true
          },
          {
            name: 'operationType',
            label: t('buyAndSell:forms.editDraft.inputs.operationType'),
            hideForResponsive: true,
            type: DataType.SELECT,
            selectOptions: enumToSelectOptionArrayDefaultValues(
              EnumTipoOperacion,
              'enums:EnumTipoOperacion',
              [EnumTipoOperacion.Compra, EnumTipoOperacion.Venta]
            ),
            required: true
          },
          {
            name: 'logisticElement.id',
            label: t('buyAndSell:forms.editDraft.inputs.logisticElement'),
            type: DataType.SELECT,
            selectOptions: formDependencies.logisticElements,
            required: true
          },
          {
            name: 'quantity',
            label: t('buyAndSell:forms.editDraft.inputs.quantity'),
            type: DataType.NUMBER,
            decimalScale: DecimalScale.ENERGY,
            required: true
          },
          // {
          //   name: 'price',
          //   label: t('buyAndSell:forms.editDraft.inputs.price'),
          //   decimalScale: DecimalScale.CURRENCY,
          //   type: DataType.NUMBER
          // },
          {
            name: 'counterpart.id',
            label: t('buyAndSell:forms.editDraft.inputs.counterpart'),
            type: DataType.SELECT,
            selectOptions: formDependencies.counterparts
          },
          {
            name: 'codTrade',
            label: t('buyAndSell:forms.editDraft.inputs.tradeCode'),
            type: DataType.TEXT,
            readOnly: true
          },
          {
            name: 'codShip',
            label: t('buyAndSell:forms.edit.inputs.codShip'),
            type: DataType.TEXT,
            readOnly: false
          }
        ]
      }
    }, [formDependencies, t])
  //#endregion

  //#region Hooks

  const { showErrorDetailListDialog, errorDetailListDialogProps } = useErrorDetailListDialog()

  const { showError, clearError, errorProps } = useHttpError()

  const { openForm, updateFormDefinition, closeForm, setFormWaiting, formProps, CustomFormDialog } =
    useFormDialog()

  const { getConfirmation, closeConfirmation } = useConfirmationDialog()

  const [startRetriesTimer, stopRetriesTimer, isRetriesTimerActive] = useRetries(
    async (pendingOperations: BuyAndSellModel[]) => {
      pendingOperations = await monitorOperations(pendingOperations)

      if (pendingOperations.length === 0) {
        isRetriesTimerActive && stopRetriesTimer()
        setGlobalNotification(undefined)
        info(t('buyAndSell:notifications.success.retriesTSOFinish'))
        return
      }
    },
    5000,
    {
      immediate: true,
      onRetry: () => {
        setGlobalNotification({
          icon: NotificationMessageIcons.LOADING,
          message: t('buyAndSell:globalNotifications.retries.tso.messages.onRetry'),
          actions: [
            {
              label: t('buyAndSell:globalNotifications.retries.tso.actions.cancel'),
              onClick: () => {
                isRetriesTimerActive && stopRetriesTimer()
                setGlobalNotification({
                  message: t('buyAndSell:globalNotifications.retries.tso.messages.onStop'),
                  actions: [
                    {
                      label: t('buyAndSell:globalNotifications.retries.tso.actions.refresh'),
                      onClick: async () => loadPage()
                    },
                    {
                      label: t('buyAndSell:globalNotifications.retries.tso.actions.close'),
                      onClick: () => {
                        setGlobalNotification(undefined)
                      }
                    }
                  ]
                })
              }
            }
          ]
        })
      },
      onFinishRetries: () => {
        setGlobalNotification({
          icon: NotificationMessageIcons.INFO,
          message: t('buyAndSell:globalNotifications.retries.tso.messages.onFinish'),
          actions: [
            {
              label: t('buyAndSell:globalNotifications.retries.tso.actions.refresh'),
              onClick: async () => loadPage()
            },
            {
              label: t('buyAndSell:globalNotifications.retries.tso.actions.close'),
              onClick: () => {
                setGlobalNotification(undefined)
              }
            }
          ]
        })
      },
      onError: () => {
        setGlobalNotification({
          icon: NotificationMessageIcons.ERROR,
          message: t('buyAndSell:globalNotifications.retries.tso.messages.onError'),
          actions: [
            {
              label: t('buyAndSell:globalNotifications.retries.tso.actions.refresh'),
              onClick: async () => loadPage()
            }
          ]
        })
      }
    }
  )

  useEffect(() => {
    loadPage()

    const getInitialFormDependencies = async () => {
      setFormDependencies({
        ...formDependencies,
        counterparts: await fetchSubjectsByTypeToItems(),
        logisticElements: await fetchLogisticElementsToItems()
      })
    }

    getInitialFormDependencies()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateRange, activeBalanceZone, activeCompany])

  useEffect(() => {
    updateFormDefinition(addFormDefinition, formDependencies.formState)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addFormDefinition])

  useEffect(() => {
    updateFormDefinition(addMassiveFormDefinition, formDependencies.formState)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addMassiveFormDefinition])

  useEffect(() => {
    updateFormDefinition(editFormDefinition, formDependencies.formState)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editFormDefinition])

  useEffect(() => {
    updateFormDefinition(editDraftFormDefinition, formDependencies.formState)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editDraftFormDefinition])

  //#endregion

  //#region Handlers

  const onSelectDateHandler = React.useCallback(
    (startDate: Date, endDate: Date, activeDateFilter?: DateFilters): void => {
      setDateRange({ start: startDate, end: endDate })
      storeDateFilterForCurrentPage({
        activeDateFilter: activeDateFilter,
        initDate: startDate,
        endDate: endDate
      })
    },
    []
  )

  const onAddClickHandler = () => {
    openForm({
      definition: addFormDefinition,
      title: t('buyAndSell:forms.add.title'),
      mode: FormDialogMode.ADD,
      isWaiting: false,
      onClose: onAddHandler
    })

    setFormDependencies({
      ...formDependencies,
      formState: {}
    })
  }

  const onAddHandler = async (item: BuyAndSellModel) => {
    if (item) {
      setFormWaiting(true)
      await add(item).then(async response => {
        if (response.success) {
          loadPage()
          info(t('buyAndSell:notifications.success.add'))
        } else {
          response.error
            ? showErrorDetailListDialog(
                t('buyAndSell:notifications.error.add'),
                [response.error],
                moment(item.date).format('DD/MM/YYYY')
              )
            : notifyError(t('buyAndSell:notifications.error.add'))
        }
        closeForm()
      })
    } else {
      closeForm()
    }
  }

  const onAddMassiveClickHandler = () => {
    openForm({
      definition: addMassiveFormDefinition,
      title: t('buyAndSell:forms.addMassive.title'),
      mode: FormDialogMode.ADD,
      onClose: onAddMassiveNominationHandler
    })

    setFormDependencies({
      ...formDependencies,
      formState: {}
    })
  }

  const onAddMassiveNominationHandler = async (data: AddMassiveBuyAndSellModel) => {
    if (data) {
      setFormWaiting(true)

      await addByTrade(data).then(async response => {
        if (response.success) {
          info(t('buyAndSell:notifications.success.addMassive'))
        } else {
          response.error
            ? showErrorDetailListDialog(t('buyAndSell:notifications.error.addMassive'), [
                response.error
              ])
            : notifyError(t('buyAndSell:notifications.error.addMassive'))
        }
        loadPage()
        closeForm()
      })
    } else {
      closeForm()
    }
  }

  const onCancelActionHandler = async (item: BuyAndSellModel) => {
    getConfirmation(ConfirmationDialogs.CANCEL).then(async () => {
      await cancel(item).then(async success => {
        if (success) {
          info(t('buyAndSell:notifications.success.cancel'))
          loadPage()
        }
      })

      closeConfirmation()
    })
  }

  const onCancelMassiveActionHandler = async (items: BuyAndSellModel[]) => {
    var totSel = items.length
    items = GetCanCancelMassive(items)

    if (items.length > 0) {
      getConfirmation(
        ConfirmationDialogs.CANCEL_MASSIVE_TOTS,
        t('confirmDialogs:CANCEL_MASSIVE_TOTS.message', { cnt1: items.length, cnt2: totSel })
      ).then(async () => {
        await cancelMassive(items).then(async (response: ServiceBatchResponse) => {
          if (response.success) {
            info(t('buyAndSell:notifications.success.cancelMassive'))
            loadPage()
          } else {
            response.errors
              ? showErrorDetailListDialog(
                  t('buyAndSell:notifications.error.cancelMassive'),
                  response.errors
                )
              : notifyError(t('buyAndSell:notifications.error.cancelMassive'))
          }
        })

        closeConfirmation()
      })
    } else {
      info(t('buyAndSell:notifications.info.notAnyCanCancelMassive'))
    }
  }

  const onSendActionHandler = async (item: BuyAndSellModel) => {
    getConfirmation(ConfirmationDialogs.SEND).then(async () => {
      await sendOperationsMassive([item]).then(async success => {
        if (success) {
          info(t('buyAndSell:notifications.success.send'))
          loadPage()
          startRetriesTimer([item])
        }
      })
      closeConfirmation()
    })
  }

  const onSendMassiveActionHandler = async (items: BuyAndSellModel[]) => {
    var totSel = items.length
    items = GetCanSendMassive(items)

    if (items.length > 0) {
      getConfirmation(
        ConfirmationDialogs.SEND_MASSIVE_TOTS,
        t('confirmDialogs:SEND_MASSIVE_TOTS.message', { cnt1: items.length, cnt2: totSel })
      ).then(async () => {
        await sendOperationsMassive(items).then(async success => {
          if (success) {
            info(t('buyAndSell:notifications.success.sendMassive'))
            loadPage()
            startRetriesTimer(items)
          }
        })
        closeConfirmation()
      })
    } else {
      info(t('buyAndSell:notifications.info.notAnyCanSendMassive'))
    }
  }

  const onDeleteActionHandler = async (item: BuyAndSellModel) => {
    getConfirmation(ConfirmationDialogs.DELETE).then(async () => {
      await deleteOperation(item).then(async response => {
        if (response.success) {
          info(t('buyAndSell:notifications.success.delete'))
          loadPage()
        } else {
          response.error
            ? showErrorDetailListDialog(t('buyAndSell:notifications.error.delete'), [
                response.error
              ])
            : notifyError(t('buyAndSell:notifications.error.cancelMassive'))
        }
      })
      closeConfirmation()
    })
  }

  const onDeleteMassiveActionHandler = async (items: BuyAndSellModel[]) => {
    var totSel = items.length
    const maxItemDelete = 20

    if (totSel > maxItemDelete) {
      notifyError(
        t('buyAndSell:notifications.error.maxDeleteLimit', {
          max: maxItemDelete
        })
      )
      return
    }

    items = GetCanDeleteMassive(items)

    if (items.length > 0) {
      getConfirmation(
        ConfirmationDialogs.DELETE_MASSIVE_TOTS,
        t('confirmDialogs:DELETE_MASSIVE_TOTS.message', { cnt1: items.length, cnt2: totSel })
      ).then(async () => {
        await deleteMassive(items).then(async (response: ServiceBatchResponse) => {
          if (response.success) {
            info(t('buyAndSell:notifications.success.deleteMassive'))
          } else {
            response.errors
              ? showErrorDetailListDialog(
                  t('buyAndSell:notifications.error.deleteMassive'),
                  response.errors
                )
              : notifyError(t('buyAndSell:notifications.error.deleteMassive'))
          }
          loadPage()
        })

        closeConfirmation()
      })
    } else {
      info(t('buyAndSell:notifications.info.notAnyCanDeleteMassive'))
    }
  }

  const onEditClickHandler = async (item: BuyAndSellModel) => {
    openForm({
      definition: editFormDefinition,
      title: t('buyAndSell:forms.edit.title'),
      mode: FormDialogMode.EDIT,
      isWaiting: true,
      onClose: onEditHandler
    })

    setFormDependencies({
      ...formDependencies,
      formState: item
    })
  }

  const onEditDraftClickHandler = async (item: BuyAndSellModel) => {
    openForm({
      definition: editDraftFormDefinition,
      title: t('buyAndSell:forms.editDraft.title'),
      mode: FormDialogMode.EDIT,
      isWaiting: true,
      onClose: onEditHandler
    })

    setFormDependencies({
      ...formDependencies,
      formState: item
    })
  }

  const onEditHandler = async (item: BuyAndSellModel) => {
    if (item) {
      setFormWaiting(true)
      await edit(item).then(async response => {
        if (response.success) {
          loadPage()
          info(t('buyAndSell:notifications.success.edit'))
        } else {
          response.error
            ? showErrorDetailListDialog(
                t('buyAndSell:notifications.error.edit'),
                [response.error],
                moment(item.date).format('DD/MM/YYYY')
              )
            : notifyError(t('buyAndSell:notifications.error.edit'))
        }
        closeForm()
      })
    } else {
      closeForm()
    }
  }

  //#endregion

  //#region DataTable definition

  const options = {
    i18n: t('globalerisLibrary:dataTable', { returnObjects: true }),
    search: true,
    elevation: 1,
    filterType: FilterType.ADVANCED,
    download: true,
    filename: normalizeFilename(t('buyAndSell:title')),
    print: false,
    selectableRows: true,
    canDelete: false,
    canEdit: false,
    fabLabel: t('buyAndSell:dataTable.fab'),
    rowsPerPage: dataTableRowsPerPage || 100,
    rowsPerPageOptions: [50, 100, 200],
    fabCustomActions: [
      {
        userPermission: UserPermission.EDIT,
        icon: <Add fontSize="small" />,
        tooltip: t('buyAndSell:dataTable.fabCustomActions.add'),
        onClick: onAddClickHandler
      },
      {
        userPermission: UserPermission.EDIT,
        icon: <PlusOne fontSize="small" />,
        tooltip: t('buyAndSell:dataTable.fabCustomActions.addMassive'),
        onClick: onAddMassiveClickHandler
      }
    ],
    rowCustomActions: [
      {
        userPermission: UserPermission.SEND,
        visibleIf: 'canSend',
        icon: <SendIcon fontSize="small" />,
        tooltip: t('buyAndSell:dataTable.customActions.send'),
        onClick: onSendActionHandler
      },
      {
        userPermission: UserPermission.EDIT,
        visibleIf: 'canCancel',
        icon: <BlockIcon fontSize="small" />,
        tooltip: t('buyAndSell:dataTable.customActions.cancel'),
        onClick: onCancelActionHandler
      },
      {
        userPermission: UserPermission.EDIT,
        visibleIf: 'canEdit',
        icon: <Edit fontSize="small" />,
        tooltip: t('buyAndSell:dataTable.customActions.edit'),
        onClick: (row: BuyAndSellModel) => {
          row.state === EnumOperationState.Borrador
            ? onEditDraftClickHandler(row)
            : onEditClickHandler(row)
        }
      },
      {
        userPermission: UserPermission.EDIT,
        visibleIf: 'canDelete',
        icon: <Delete fontSize="small" />,
        tooltip: t('buyAndSell:dataTable.customActions.delete'),
        onClick: onDeleteActionHandler
      }
    ],
    selectToolbarCustomActions: [
      {
        userPermission: UserPermission.SEND,
        icon: <SendIcon fontSize="small" />,
        tooltip: t('buyAndSell:dataTable.customActions.send'),
        onClick: onSendMassiveActionHandler
      },
      {
        userPermission: UserPermission.EDIT,
        icon: <BlockIcon fontSize="small" />,
        tooltip: t('buyAndSell:dataTable.customActions.cancel'),
        onClick: onCancelMassiveActionHandler
      },
      {
        userPermission: UserPermission.EDIT,
        icon: <Delete fontSize="small" />,
        tooltip: t('buyAndSell:dataTable.customActions.delete'),
        onClick: onDeleteMassiveActionHandler
      }
    ],
    onFilterChange: (filter: ColumnFilter[]) => {
      storeDataTableFilterForCurrentPage(filter)
    },
    onSearchChange: (search: string) => {
      storeDataTableSearchForCurrentPage(search)
    },
    onRowsPerPageChange: (rowsPerPage: number) => {
      storeDataTableRowsPerPageForCurrentPage(rowsPerPage)
    }
  }

  const columns: DataTableColumn[] = [
    {
      name: 'id',
      display: false
    },
    {
      name: 'actions',
      label: '',
      hideForResponsive: true,
      type: DataType.HTML
    },
    {
      name: 'date',
      label: t('buyAndSell:dataTable.columns.date'),
      type: DataType.DATE
    },
    {
      name: 'operationType',
      label: t('buyAndSell:dataTable.columns.operationType'),
      type: DataType.SELECT,
      selectOptions: enumToSelectOptionArrayDefaultValues(
        EnumTipoOperacion,
        'enums:EnumTipoOperacion',
        [EnumTipoOperacion.Compra, EnumTipoOperacion.Venta]
      )
    },
    {
      name: 'logisticElement.description',
      label: t('buyAndSell:dataTable.columns.logisticElement'),
      type: DataType.TEXT,
      columnWidth: 30
    },
    {
      name: 'logisticElement.id',
      label: t('buyAndSell:dataTable.columns.logisticElement'),
      type: DataType.TEXT,
      display: false
    },
    {
      name: 'codShip',
      label: t('buyAndSell:dataTable.columns.codShip'),
      type: DataType.TEXT,
      columnWidth: 200
    },
    {
      name: 'quantity',
      label: t('buyAndSell:dataTable.columns.quantity'),
      type: DataType.NUMBER,
      decimalScale: DecimalScale.ENERGY,
      columnWidth: 7
    },
    {
      name: 'sentQuantity',
      label: t('buyAndSell:dataTable.columns.quantitySent'),
      type: DataType.NUMBER,
      decimalScale: DecimalScale.ENERGY,
      hideForResponsive: true,
      columnWidth: 9
    },
    {
      name: 'quantityTSO',
      label: t('buyAndSell:dataTable.columns.quantityTSO'),
      type: DataType.NUMBER,
      decimalScale: DecimalScale.ENERGY,
      hideForResponsive: true,
      columnWidth: 9
    },
    {
      name: 'codTrade',
      label: t('buyAndSell:dataTable.columns.tradeCode'),
      type: DataType.TEXT,
      columnWidth: 200
    },
    {
      name: 'codPosition',
      label: t('buyAndSell:dataTable.columns.codePosition'),
      type: DataType.TEXT,
      columnWidth: 200
    },
    {
      name: 'counterpart.description',
      label: t('buyAndSell:dataTable.columns.counterpart'),
      type: DataType.TEXT
    },
    {
      name: 'stateBuyAndSell',
      label: t('buyAndSell:dataTable.columns.state'),
      type: DataType.MULTISELECT,
      selectOptions: enumToSelectOptionArray(
        EnumEstadoOperacionBuyAndSell,
        'enums:EnumEstadoOperacionBuyAndSell'
      )
    },
    {
      name: 'registerDate',
      label: t('buyAndSell:dataTable.columns.registerDate'),
      type: DataType.DATE
    }
  ]

  //#endregion

  //#region Functions

  const loadPage = async () => {
    if (flanges.length === 0) {
      const getBuyAndSells = async () => {
        customFilter === undefined
          ? setOperations(
              await fetchBuyAndSellOperationsBuscador({
                startDate: calculateGasDay(dateRange.start),
                endDate: calculateGasDay(dateRange.end)
              })
            )
          : setOperations(
              await fetchBuyAndSellOperationsBuscador({
                startDate: customFilter.startDate,
                endDate: customFilter.endDate,
                counterparts: customFilter.counterparts,
                operationTypes: customFilter.operationTypes,
                state: customFilter.state,
                registerDate: customFilter.registerDate,
                tradeCode: customFilter.tradeCode
              })
            )
      }

      getBuyAndSells()
    } else {
      const getBuyAndSellsByIds = async () => {
        setOperations(await fetchBuyAndSellOperationsByIds(flanges))
      }
      getBuyAndSellsByIds()
    }
  }

  const monitorOperations = async (pendingOperations: BuyAndSellModel[]) => {
    let currentOperations = operations

    return await checkOperationsState(
      pendingOperations.map(op => op.id),
      [EnumOperationState.Enviada, EnumOperationState.Rechazado, EnumOperationState.EnviadoKO]
    ).then(changedOperations => {
      changedOperations.forEach((changedOperation: BuyAndSellModel) => {
        if (currentOperations && currentOperations !== undefined) {
          const index = currentOperations.findIndex(x => x.id === changedOperation.id)

          if (index !== -1) {
            currentOperations = [
              ...currentOperations!.slice(0, index),
              completeDataTableActions({ ...changedOperation }),
              ...currentOperations!.slice(index + 1)
            ]
            setOperations(currentOperations)
          }
        }

        const pendingIndex = pendingOperations.findIndex(x => x.id === changedOperation.id)

        pendingOperations = [
          ...pendingOperations!.slice(0, pendingIndex),
          ...pendingOperations!.slice(pendingIndex + 1)
        ]
      })

      return pendingOperations
    })
  }

  const fetchBuyAndSellOperationsBuscador = async ({
    startDate,
    endDate,
    counterparts,
    operationTypes,
    state,
    registerDate,
    tradeCode
  }: {
    startDate: Date
    endDate: Date
    counterparts?: number[]
    operationTypes?: number[]
    state?: number[]
    registerDate?: Date
    tradeCode?: { label: string; value: any }
  }): Promise<BuyAndSellModel[] | null> => {
    setIsFetching(true)
    setOperations(null) //Needed to activate skeleton on DataTable
    return await searchBuyAndSellOperations({
      startDate,
      endDate,
      counterparts,
      operationTypes,
      state,
      registerDate,
      tradeCode: tradeCode?.label
    }).then(response => {
      setIsFetching(false)

      if (response.success) {
        clearError()
        if (response.data !== undefined) {
          return response.data?.map((item: BuyAndSellModel) => {
            return completeDataTableActions(item)
          })
        } else {
          return []
        }
      } else {
        response.error &&
          showError({
            errorCode: response.error.code,
            onRefresh: async () => loadPage()
          })
        return []
      }
    })
  }

  const fetchBuyAndSellOperationsByIds = async (
    ids: number[]
  ): Promise<BuyAndSellModel[] | null> => {
    setIsFetching(true)
    setOperations(null) //Needed to activate skeleton on DataTable
    return await fetchBuyAndSellListItemsByIds(ids).then(response => {
      setIsFetching(false)

      if (response.success) {
        clearError()
        if (response.data !== undefined) {
          return response.data?.map((item: BuyAndSellModel) => {
            return completeDataTableActions(item)
          })
        } else {
          return []
        }
      } else {
        response.error &&
          showError({
            errorCode: response.error.code,
            onRefresh: async () => loadPage()
          })
        return []
      }
    })
  }

  const completeDataTableActions = (item: BuyAndSellModel): BuyAndSellModel => {
    return {
      ...item,
      actions:
        item.actionsConfiguration &&
        item.actionsConfiguration.length > 0 &&
        generateIconsFromConfiguration(
          item,
          filterApplicableActionsConfiguration(item, item.actionsConfiguration),
          new Map<ActionIcons, Function>([])
        )
    }
  }

  const searchFilterInputs: Input[] = [
    {
      name: 'operationTypes',
      label: t('translation:components.searchFilter.inputs.operationType'),
      type: DataType.MULTISELECT,
      selectOptions: enumToSelectOptionArrayDefaultValues(
        EnumTipoOperacion,
        'enums:EnumTipoOperacion',
        [EnumTipoOperacion.Compra, EnumTipoOperacion.Venta]
      )
    },
    {
      name: 'counterparts',
      label: t('translation:components.searchFilter.inputs.counterpart'),
      type: DataType.MULTISELECT,
      selectOptions: formDependencies.counterparts
    },
    {
      name: 'state',
      label: t('translation:components.searchFilter.inputs.stateBuyAndSell'),
      type: DataType.MULTISELECT,
      clearable: true,
      selectOptions: enumToSelectOptionArray(
        EnumEstadoOperacionBuyAndSellSearchFilter,
        'enums:EnumEstadoOperacionBuyAndSell'
      )
    },
    {
      name: 'registerDate',
      label: t('translation:components.searchFilter.inputs.registerDate'),
      type: DataType.DATE
    },
    {
      name: 'tradeCode',
      label: t('translation:components.searchFilter.inputs.tradeCode'),
      type: DataType.SEARCHFIELD,
      clearable: true,
      apiCall: fetchTradesByCode,
      apiCallDelay: 2000,
      apiCallMinQueryLength: 5,
      placeholder: t('translation:components.searchFilter.placeholder.tradeCode'),
      helpText: t('translation:components.searchFilter.helpTexts.tradeCode')
    }
  ]

  const fetchNominations = async (filterToApply: {
    startDate: Date
    endDate: Date
    operationTypes?: EnumOperationType[]
    counterparts?: any
    state?: any
    registerDate?: any
    tradeCode?: any
  }) => {
    return await fetchBuyAndSellOperationsBuscador({
      startDate: filterToApply.startDate,
      endDate: filterToApply.endDate,
      counterparts: filterToApply.counterparts,
      operationTypes: filterToApply.operationTypes,
      state: filterToApply.state,
      registerDate: filterToApply.registerDate,
      tradeCode: filterToApply.tradeCode
    })
  }

  const handleShowResults = (results: any[]) => {
    // Remount the DataTable so the previous row selection is cleared
    setDataTableKey(prev => prev + 1)
    setOperations(results)
  }
  //#endregion

  return (
    <Page notification={globalNotification}>
      <PageHeader title={t('buyAndSell:title')}>
        {flanges.length === 0 ? (
          <>
            <DateFilter
              inactive={customFilter}
              enabled={!isFetching}
              initialFilter={
                dateFilter?.activeDateFilter === null || dateFilter?.activeDateFilter === undefined
                  ? DateFilters.TODAY
                  : dateFilter.activeDateFilter
              }
              onSelectDate={onSelectDateHandler}
              filters={[
                {
                  filter: DateFilters.TODAY
                },
                {
                  filter: DateFilters.TOMORROW
                },
                { filter: DateFilters.WEEK }
              ]}
            ></DateFilter>
            <CustomFilterV2
              onFetchData={fetchNominations}
              onShowResults={handleShowResults}
              additionalFilters={searchFilterInputs}
              appliedFilter={customFilter}
              enabled={!isFetching}
            />
            {/* <CustomFilter
              onApplyFilter={onApplyCustomFilter}
              onPrevalidateFilter={onPrevalidateCustomFilter}
              additionalFilters={searchFilterInputs}
              appliedFilter={customFilter}
              enabled={!isFetching}
            ></CustomFilter> */}
          </>
        ) : (
          <Button
            size="small"
            onClick={() => {
              history.goBack()
            }}
          >
            {t('buyAndSell:navigation.backNomination')}
          </Button>
        )}
      </PageHeader>

      <DataTable
        key={dataTableKey}
        data={operations}
        columns={columns}
        options={{
          ...options,
          analytics: {
            customer: getCustomerDomain(),
            view: flanges.length === 0 ? 'BuyAndSell Data Table' : 'BuyAndSell Flange Data Table'
          }
        }}
        info={DataTableErrorHandler(errorProps)}
        userPermissions={getUserPermissions(Pages.BuyAndSell)}
        appliedFilter={dataTableFilter}
        appliedSearch={dataTableSearch}
      />

      <CustomFormDialog {...formProps}></CustomFormDialog>
      <ErrorDetailList {...errorDetailListDialogProps}></ErrorDetailList>
    </Page>
  )
}
